import { createConfigStore } from './store/config-store';
import { getBrandConfig } from './get-brand-config';
import { parseEnv } from './env/env-to-store';
import type { ConfigTypes } from './store/config-store.types';
import type { Brand } from './@types/Brand.types';
import { isBrand } from './@types/Brand.types';
import type { Context } from './@types/Context.types';
import { Contexts } from './@types/Context.types';

type ConfigStore = ReturnType<typeof createConfigStore>;

const isServer = () => typeof window === 'undefined';

/**
 * `ConfigService` handles async server-side bootstrapping and sync client-side initialization of configuration.
 */
export class ConfigService {
  private static _store: ConfigStore | null = null; // Scoped store for server requests.
  private static _clientStore: ConfigStore | null = null; // Singleton store on the client.

  private static _serverBrandStores: Map<Brand, ConfigStore> = new Map();

  /**
   * Boots up the configuration on the server.
   * Resolves the tenant dynamically, then initializes the store.
   */
  static async bootstrap(
    contexts: Context[] = [...Contexts]
  ): Promise<typeof ConfigService> {
    let brand: Brand | undefined;

    if (isServer()) {
      try {
        const { getRequestTenant } = await import(
          './site/multi-tenant/get-request-tenant'
        );
        brand = await getRequestTenant();
      } catch (error) {
        console.warn('Failed to resolve tenant dynamically:', error);
      }

      if (brand && !ConfigService._serverBrandStores.has(brand)) {
        console.info(
          ` ✓ ConfigService :: bootstrap : ${brand} – ${process.env.NEXT_PUBLIC_ENV} – 📦 v${process.env.pkg_version}`
        );
      }
    }

    // Initialize with the resolved tenant
    ConfigService.initialize(brand, contexts, !isServer());

    return ConfigService;
  }

  /**
   * Initializes the store and respects the specified runtime environment (client/server).
   *
   * @param brand - Optional tenant/brand
   * @param contexts - Configuration contexts
   * @param isClient - Explicitly declare client-side initialization
   */
  static initialize(
    brand?: Brand,
    contexts: Context[] = [...Contexts],
    isClient = true
  ): typeof ConfigService {
    // Fallback to environment-specified brand
    brand ??= (process.env.SITE || process.env.NEXT_PUBLIC_SITE) as Brand;

    if (!isBrand(brand)) {
      console.warn(
        `Invalid brand "${brand}" detected. Defaulting to "axofinans.se".`
      );
      brand = 'axofinans.se';
    }

    // Retrieve brand-specific configuration
    const config = getBrandConfig(brand, contexts);

    // Add parsed environment variables to the configuration
    config.env = parseEnv();

    // Initialize the store based on the runtime (server vs. client)
    if (!isClient) {
      if (!ConfigService._serverBrandStores.has(brand)) {
        // Create and cache the brand's store
        const store = createConfigStore({ config });
        ConfigService._serverBrandStores.set(brand, store);

        console.info(` ✓ ConfigService :: server store initialized : ${brand}`);
      }

      ConfigService._store = ConfigService._serverBrandStores.get(brand)!;
    } else {
      // Memoized singleton on the client
      ConfigService._store = ConfigService._clientStore ??= createConfigStore({
        config,
      });

      console.info(` ✓ ConfigService :: client store initialized : ${brand}`);
    }

    return ConfigService;
  }

  /**
   * Retrieves the active store (must call `initialize` or `bootstrap()` first).
   */
  private static getStore(): ConfigStore {
    if (!ConfigService._store) {
      throw new Error(
        'ConfigService is uninitialized. Call `ConfigService.bootstrap()` or `initialize()` first.'
      );
    }
    return ConfigService._store;
  }

  /**
   * Exposes the `register` API for adding new values to the store.
   */
  static register<K extends keyof ConfigTypes>(
    key: K,
    value: ConfigTypes[K]
  ): void {
    ConfigService.getStore().getState().register(key, value);
  }

  /**
   * Exposes the `get` API for retrieving values from the store.
   */
  static get<K extends keyof ConfigTypes>(key: K): ConfigTypes[K] {
    return ConfigService.getStore().getState().get(key);
  }

  /**
   * Retrieves all configuration values from the store.
   */
  static getAll(): Partial<ConfigTypes> {
    return ConfigService.getStore().getState().getAll();
  }
}
